import { useEffect, useState } from 'react';

import {
  Alert,
  Button,
  Center,
  Container,
  Image,
  Loader,
  Paper,
  Stack,
  Text,
  Title,
} from '@mantine/core';

import * as Sentry from '@sentry/react';

import { vnextApiClient } from '@/services/vnext-api';
import { getConfirmationToken } from '@/utils/confirmationToken';
import { toError } from '@/utils/errors';

const NO_LINK_MESSAGE =
  'This link can’t be used. Ask the person who set up the notification to send a new one.';

type State =
  | { phase: 'loading' }
  | { phase: 'ready'; email: string }
  | { phase: 'confirming'; email: string }
  | { phase: 'done'; email: string }
  | { phase: 'invalid'; message: string };

/**
 * Where the owner of a notification copy address lands from the emailed link.
 *
 * Public: the person clicking is not a platform user. Opening the page only
 * shows which address is about to be confirmed — it is confirmed by the button,
 * because mail scanners open links in messages and must not be able to confirm an
 * address on its owner's behalf.
 *
 * The token arrives in the URL fragment and is lifted out before anything can
 * record the URL (see `captureConfirmationToken`), so it is read from there, not
 * from `window.location`. It is sent only in request bodies and appears in no
 * message shown or reported.
 */
export function ConfirmNotificationAddressPage() {
  const [token] = useState(getConfirmationToken);
  const [state, setState] = useState<State>(
    token ? { phase: 'loading' } : { phase: 'invalid', message: NO_LINK_MESSAGE }
  );
  const [confirmError, setConfirmError] = useState<string | null>(null);

  useEffect(() => {
    if (!token) {
      return undefined;
    }
    let cancelled = false;
    vnextApiClient
      .previewNotificationAddress(token)
      .then(({ email }) => {
        if (!cancelled) {
          setState({ phase: 'ready', email });
        }
      })
      .catch((err) => {
        // A dead link is the expected failure, not an error worth reporting.
        if (!cancelled) {
          setState({ phase: 'invalid', message: toError(err).message });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  const confirm = async () => {
    if (!token || state.phase !== 'ready') {
      return;
    }
    const { email } = state;
    setConfirmError(null);
    setState({ phase: 'confirming', email });
    try {
      await vnextApiClient.confirmNotificationAddress(token);
      setState({ phase: 'done', email });
    } catch (err) {
      // The preview just succeeded, so a failure here is worth knowing about,
      // and worth letting the person try again.
      Sentry.captureException(toError(err));
      setConfirmError(toError(err).message);
      setState({ phase: 'ready', email });
    }
  };

  return (
    <Container size="xs" py="xl">
      <Paper withBorder radius="md" p="xl">
        <Stack gap="md" align="stretch">
          <Image
            src="/assets/gti/logo-primary-dark.svg"
            alt="Ground Truth Intelligence"
            h={32}
            fit="contain"
            style={{ alignSelf: 'flex-start' }}
          />

          {state.phase === 'loading' && (
            <Center py="lg">
              <Loader size="sm" />
            </Center>
          )}

          {(state.phase === 'ready' || state.phase === 'confirming') && (
            <>
              <Title order={3}>Confirm this address</Title>
              <Text>
                <Text component="span" fw={600}>
                  {state.email}
                </Text>{' '}
                will receive GTI report notifications once you confirm.
              </Text>
              {confirmError && (
                <Alert color="red" title="Couldn’t confirm">
                  {confirmError}
                </Alert>
              )}
              <Button onClick={() => void confirm()} loading={state.phase === 'confirming'}>
                Confirm
              </Button>
            </>
          )}

          {state.phase === 'done' && (
            <>
              <Title order={3}>Address confirmed</Title>
              <Text>
                <Text component="span" fw={600}>
                  {state.email}
                </Text>{' '}
                will now receive GTI report notifications. You can close this page.
              </Text>
            </>
          )}

          {state.phase === 'invalid' && (
            <>
              <Title order={3}>This link can’t be used</Title>
              <Text>{state.message}</Text>
            </>
          )}
        </Stack>
      </Paper>
    </Container>
  );
}
